import { LuLayoutDashboard, LuTrendingUp, LuTrendingDown, LuActivity, LuPercent } from "react-icons/lu";
import React, { useState, useEffect } from "react";
import api from "../services/api";
import { toast } from "react-toastify";
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useTheme } from "../ThemeContext";

const Home = ({ isSidebarOpen = true }) => {
  const { theme } = useTheme();
  // Download Database
  const handleDownloadDb = async () => {
    try {
      const response = await api.get(
        "/admin/download-db",
        {
          responseType: 'blob', // Important for file download
        }
      );

      // Create a URL for the blob
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'database.zip'); // File name
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Database downloaded successfully");
    } catch (error) {
      console.error("Download failed:", error);
      toast.error("Failed to download database");
    }
  };


  const [stats, setStats] = useState({
    balance: 0,
    margin_used: 0,
    total_balance: 0,
    today_pnl: 0,
    today: {
      trades: 0,
      wins: 0,
      pnl: 0,
      success_rate: 0
    },
    all_time: {
      trades: 0,
      wins: 0,
      pnl: 0,
      success_rate: 0
    },
    chartData: []
  });

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await api.get("/OR/dashboard/stats");
      if (response.data.status === "success") {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error("Failed to fetch dashboard stats", error);
      // toast.error("Failed to load dashboard stats"); // Optional: suppress to avoid noise
    }
  };

  const formatCurrency = (val) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2
    }).format(val || 0);
  };

  const renderStatCard = (title, value, subtitle, colorClass = "text-primary", icon = null) => (
    <div className="card border-0 shadow-sm h-100">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-start">
          <div>
            <h6 className="text-muted small text-uppercase mb-2">{title}</h6>
            <h3 className={`mb - 0 fw - bold ${colorClass} `}>{value}</h3>
            {subtitle && <small className="text-muted">{subtitle}</small>}
          </div>
          {icon && <div className={`p - 2 rounded - circle bg - light ${colorClass.replace('text-', 'text-')} `}>{icon}</div>}
        </div>
      </div>
    </div>
  );

  return (
    <>
      <div className={`${isSidebarOpen ? "col-lg-9" : "col-lg-12"} pt - 4 pb - 2 pb - sm - 4`}>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2 className="d-flex align-items-center mb-0">
            <LuLayoutDashboard className="text-primary me-2" size={30} />
            Dashboard
          </h2>
          <button
            onClick={handleDownloadDb}
            className="btn btn-sm btn-info d-flex align-items-center gap-2"
          >
            Download DB
          </button>
        </div>

        {/* Primary Stats Row */}
        <div className="row g-3 mb-4">
          <div className="col-12 col-md-4">
            {renderStatCard(
              "Available Balance",
              formatCurrency(stats.balance),
              `Total: ${formatCurrency(stats.total_balance)} `,
              "text-primary"
            )}
          </div>
          <div className="col-12 col-md-4">
            {renderStatCard(
              "Margin Used",
              formatCurrency(stats.margin_used),
              null,
              "text-warning"
            )}
          </div>
          <div className="col-12 col-md-4">
            {renderStatCard(
              "Today's P/L",
              formatCurrency(stats.today_pnl),
              "Realized (Approx)",
              stats.today_pnl >= 0 ? "text-success" : "text-danger"
            )}
          </div>
        </div>

        {/* Detailed Stats Row */}
        <div className="row g-3 mb-4">
          {/* Today's Performance */}
          <div className="col-12 col-md-6">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body p-4">
                <h6 className="fw-bold ">
                  <LuActivity className="me-2 text-info" /> Today's Performance
                </h6>
                <div className="row g-3">
                  <div className="col-4">
                    <div className="p-3 bg-light rounded text-center">
                      <small className="text-muted d-block mb-1">Trades</small>
                      <h5 className="mb-0 fw-bold">{stats.today?.trades || 0}</h5>
                    </div>
                  </div>
                  <div className="col-4">
                    <div className="p-3 bg-light rounded text-center">
                      <small className="text-muted d-block mb-1">Wins</small>
                      <h5 className="mb-0 fw-bold text-success">{stats.today?.wins || 0}</h5>
                    </div>
                  </div>
                  <div className="col-4">
                    <div className="p-3 bg-light rounded text-center">
                      <small className="text-muted d-block mb-1">Win Rate</small>
                      <h5 className="mb-0 fw-bold text-primary">{stats.today?.success_rate || 0}%</h5>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* All Time Performance */}
          <div className="col-12 col-md-6">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body p-4">
                <h6 className=" fw-bold">
                  <LuTrendingUp className="me-2 text-primary" /> All Time Performance
                </h6>
                <div className="row g-3">
                  <div className="col-4">
                    <div className="p-3 bg-light rounded text-center">
                      <small className="text-muted d-block mb-1">Total Trades</small>
                      <h5 className="mb-0 fw-bold">{stats.all_time?.trades || 0}</h5>
                    </div>
                  </div>
                  <div className="col-4">
                    <div className="p-3 bg-light rounded text-center">
                      <small className="text-muted d-block mb-1">Total Wins</small>
                      <h5 className="mb-0 fw-bold text-success">{stats.all_time?.wins || 0}</h5>
                    </div>
                  </div>
                  <div className="col-4">
                    <div className="p-3 bg-light rounded text-center">
                      <small className="text-muted d-block mb-1">Win Rate</small>
                      <h5 className="mb-0 fw-bold text-primary">{stats.all_time?.success_rate || 0}%</h5>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Charts Section */}
        {stats.chartData && stats.chartData.length > 0 && (
          <div className="row mb-4">
            <div className="col-12">
              <div className="card border-0 shadow-sm">
                <div className="card-body">
                  <HighchartsReact
                    highcharts={Highcharts}
                    options={{
                      chart: {
                        type: 'column',
                        backgroundColor: 'transparent',
                        style: {
                          fontFamily: 'inherit'
                        }
                      },
                      title: {
                        text: 'Weekly Performance',
                        style: {
                          color: theme === 'dark' ? '#f8f9fa' : '#212529'
                        }
                      },
                      xAxis: {
                        categories: stats.chartData.map(d => d.displayDate),
                        labels: {
                          style: {
                            color: theme === 'dark' ? '#adb5bd' : '#6c757d'
                          }
                        },
                        lineColor: theme === 'dark' ? '#495057' : '#dee2e6'
                      },
                      yAxis: [{
                        title: {
                          text: 'Invested Amount',
                          style: {
                            color: Highcharts.getOptions().colors[0]
                          }
                        },
                        labels: {
                          style: {
                            color: Highcharts.getOptions().colors[0]
                          }
                        },
                        gridLineColor: theme === 'dark' ? '#343a40' : '#e9ecef'
                      }, { // Secondary yAxis
                        title: {
                          text: 'Profit / Loss',
                          style: {
                            color: theme === 'dark' ? '#2ecc71' : '#198754'
                          }
                        },
                        labels: {
                          style: {
                            color: theme === 'dark' ? '#2ecc71' : '#198754'
                          }
                        },
                        opposite: true,
                        gridLineWidth: 0
                      }],
                      tooltip: {
                        shared: true,
                        backgroundColor: theme === 'dark' ? '#212529' : '#ffffff',
                        style: {
                          color: theme === 'dark' ? '#f8f9fa' : '#212529'
                        },
                        borderColor: theme === 'dark' ? '#495057' : '#dee2e6'
                      },
                      plotOptions: {
                        column: {
                          borderRadius: 5,
                          borderWidth: 0
                        }
                      },
                      legend: {
                        itemStyle: {
                          color: theme === 'dark' ? '#e9ecef' : '#212529'
                        },
                        itemHoverStyle: {
                          color: theme === 'dark' ? '#fff' : '#000'
                        }
                      },
                      series: [{
                        name: 'Invested',
                        type: 'column',
                        yAxis: 0,
                        data: stats.chartData.map(d => d.invested),
                        tooltip: { valuePrefix: '₹' }
                      }, {
                        name: 'Profit/Loss',
                        type: 'spline',
                        yAxis: 1,
                        data: stats.chartData.map(d => d.pnl),
                        tooltip: { valuePrefix: '₹' },
                        color: theme === 'dark' ? '#2ecc71' : '#198754'
                      }]
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Existing Content */}
        <div className="my-4">
          <div className="card border-0 ">
            <div className="card-body">
              <div className="">
                <p className="text-success mb-3">
                  Today is the end of the Republic. The end of a regime that
                  acquiesces to disorder. At this very moment, in a system far
                  from here, the New Republic lies to the galaxy while secretly
                  supporting the treachery of the loathsome Resistance. This
                  fierce machine which you have built, upon which we stand, will
                  bring an end to the Senate, to their cherished fleet.
                </p>
                <p className="text-danger fw-bold">
                  All remaining systems will bow to the First Order and remember
                  this as the last day of the Republic!
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Home;
